import { supabase } from '@/lib/supabase';
import { API_BASE_URL } from '@/config';
import { toast } from 'sonner';

export interface PhLogoUploadResult {
  publicUrl: string;
  dataUrl: string;
  folder: string;
  storagePath: string;
}

export class PhLogoStorageService {
  public static BUCKET_NAME = 'ph-logos';

  /**
   * Sanitiza el nombre de la copropiedad para nombrar su carpeta en Supabase Storage
   * Ej: "Conjunto Residencial Torres del Parque" -> "torres-del-parque"
   */
  public static getPhFolderName(copropiedadNombre?: string): string {
    if (!copropiedadNombre || !copropiedadNombre.trim()) {
      return 'ph-default';
    }
    return copropiedadNombre
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'ph-default';
  }

  /**
   * Convierte un archivo File en Base64 Data URL para incrustación local inmediata
   */
  public static fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  }

  /**
   * Sube el logo institucional de la copropiedad a Supabase Storage
   * en el bucket 'ph-logos', organizándolo en una carpeta individual por PH.
   */
  public static async uploadLogo(
    file: File,
    copropiedadNombre: string,
    copropiedadNit?: string
  ): Promise<PhLogoUploadResult> {
    // 1. Validaciones previas de formato y tamaño
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Formato no admitido. Por favor seleccione un archivo PNG, JPG, WebP o SVG.');
    }

    if (file.size > 5 * 1024 * 1024) {
      throw new Error('El archivo del logo no debe superar los 5 MB.');
    }

    // 2. Generar Data URL en memoria para renderizado sincrónico en jsPDF
    const dataUrl = await this.fileToDataUrl(file);

    // 3. Estructura de carpetas por PH en el bucket
    const folder = this.getPhFolderName(copropiedadNombre);
    const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
    const fileName = `logo_${Date.now()}.${ext}`;
    const storagePath = `${folder}/${fileName}`;

    let publicUrl = '';

    // 4. Intento 1: Subida directa a Supabase Storage con cliente Web
    try {
      const { data, error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: true,
          contentType: file.type
        });

      if (!error && data) {
        const { data: pubData } = supabase.storage
          .from(this.BUCKET_NAME)
          .getPublicUrl(storagePath);
        publicUrl = pubData.publicUrl;
      } else {
        throw new Error(error?.message || 'Error en subida directa');
      }
    } catch (directErr) {
      console.warn('[LogoStorage] Fallback a endpoint de backend para subida con privilegios:', directErr);

      // 5. Intento 2: Fallback al backend API con service role key
      try {
        const formData = new FormData();
        formData.append('file', file);

        const params = new URLSearchParams({
          copropiedad_nombre: copropiedadNombre,
          ...(copropiedadNit ? { copropiedad_nit: copropiedadNit } : {})
        });

        const baseUrl = API_BASE_URL.replace(/\/+$/, '');
        const res = await fetch(`${baseUrl}/api/v1/demand-letter/upload-ph-logo?${params.toString()}`, {
          method: 'POST',
          body: formData
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.detail || 'Error en el servidor al subir logo.');
        }

        const resData = await res.json();
        publicUrl = resData.public_url || '';
      } catch (backendErr: any) {
        console.error('[LogoStorage] Ambos métodos de subida fallaron:', backendErr);
        // Si la red falla por completo, conservamos el dataUrl local para que el usuario no pierda el logo
        publicUrl = dataUrl;
        toast.warning('El logo se conservará localmente en el navegador para sus documentos.');
      }
    }

    return {
      publicUrl,
      dataUrl,
      folder,
      storagePath
    };
  }

  /**
   * Elimina un logo de Supabase Storage
   */
  public static async deleteLogo(storagePath: string): Promise<boolean> {
    try {
      const { error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .remove([storagePath]);
      return !error;
    } catch (err) {
      console.warn('[LogoStorage] Error al eliminar logo:', err);
      return false;
    }
  }
}

export default PhLogoStorageService;

